import React, { useEffect, useRef, useState } from "react";
import { Avatar } from "@mui/material";
import HeyGenStream from "../Stream/HygenStream";
import TavusStream from "../Stream/TavusStream";
import { MdCallEnd, MdKeyboardVoice } from "react-icons/md";
import { AiOutlineAudioMuted } from "react-icons/ai";
import { RiUserAddFill } from "react-icons/ri";
import { useLocation, useNavigate } from "react-router-dom";
import { getSocket, onSocketDisconnect } from "../../socket";
import toast from "react-hot-toast";
import { playAudioWithGlobalContext } from "../../utils/audioContext";
import LocalCameraPreview from "../Stream/SelfStream";
import VideocamIcon from '@mui/icons-material/Videocam';
import VideocamOffOutlinedIcon from '@mui/icons-material/VideocamOffOutlined';
import { useDispatch, useSelector } from "react-redux";
import { setSocketState } from "../../redux/slice/socketSlice";
import { obs, ObsEvent, LatencyTracker } from "../../services/observability";
import Cookie from "../../utils/Cookie";
import PayrollModal from "../Modals/ShowPayrollModal";
import useGlobalStore from "../../hooks/useGlobalStore";
import { AnimatePresence, motion } from "framer-motion";

// PAL boot is typically 5–7s after the Daily room exists. The room-connect
// timeout used to clear on our own join, so the ring stopped before the face
// arrived. This window covers Daily join plus that boot, and stays under the
// 30s server stale timeout that starts when the media session is created.
const TAVUS_AVATAR_JOIN_TIMEOUT_MS = 20000;

export default function LiveAvatar({ chatId = "", userId = "", name = "", profilePic = "", isBackPressed }) {
    const dispatch = useDispatch();
    const { TOGGLE_PAYROLL_MODAL, toggleForModal } = useGlobalStore();

    const { isInitialized, user } = useSelector(state => state.socketSlice);
    const videoCallStatus = Cookie.getCookie('video_call_start');
    const childRef = useRef(null);
    const streamRef = useRef(null);
    const navigator = useNavigate();
    const { search } = useLocation();
    const query = new URLSearchParams(search);
    const creatorId = query.get("id");
    const [sessionData, setSessionData] = useState(null);
    const [mute, setMute] = useState(false);
    const [isCallActive, setIsCallActive] = useState(false);
    const [callDuration, setCallDuration] = useState(0);
    const [shouldRing, setShouldRing] = useState(false);
    const [avatarIsSpeaking, setAvatarIsSpeaking] = useState(false);
    const [isCameraOn, setIsCameraOn] = useState(false);
    const [permissionStatus, setPermissionStatus] = useState("pending");
    const [callPhase, setCallPhase] = useState("requesting_permissions");
    // Refs for persistent objects
    const audioStreamRef = useRef(null);
    const micCtxRef = useRef(null);
    const micSourceRef = useRef(null);
    const deepgramSocketRef = useRef(null);


    // Latency tracking refs
    const lastAudioByteTimeRef = useRef(null);
    const deepgramConnectTrackerRef = useRef(null);

    // Create a ref for the latest avatarIsSpeaking state
    const avatarIsSpeakingRef = useRef(avatarIsSpeaking);
    const keepAliveIntervalRef = useRef(null);
    const isLiveKitConnectedRef = useRef(false);
    const turnStartedWithLiveKitRef = useRef(false);
    const backgroundHideTimerRef = useRef(null);
    const stopSessionRef = useRef(() => { });
    const sessionIdRef = useRef(null);
    const sessionProviderRef = useRef(null);
    const roomConnectTimeoutRef = useRef(null);
    const tavusSessionRef = useRef(null);
    const tavusAvatarJoinedRef = useRef(false);
    const onTavusAvatarJoinedRef = useRef(() => {});
    const isStoppingRef = useRef(false);
    // Timestamp of the in-flight reply (0 = none), so barge-in also works during the 1-3s
    // "thinking" window. A timestamp not a flag: a turn that errors never reaches
    // speak_started, and a stuck flag would interrupt every later turn.
    const awaitingReplyRef = useRef(0);
    const AWAITING_REPLY_MAX_MS = 15000;
    // Deepgram Flux endpoints mid-sentence, so one sentence arrives as several turns
    // 1.1-1.7s apart. Treating those as barge-in cancelled each reply before its first
    // token and the call looked frozen. Genuinely separate utterances were 3.8s+ apart.
    const BARGE_IN_MIN_REPLY_AGE_MS = 2000;
    // Fragments of the sentence already sent, so a superseding turn asks the full
    // question instead of only its tail ("you" instead of "how are you").
    const pendingTranscriptRef = useRef('');

    useEffect(() => {
        avatarIsSpeakingRef.current = avatarIsSpeaking;
    }, [avatarIsSpeaking]);

    useEffect(() => {
        sessionIdRef.current = sessionData?.session_id ?? null;
    }, [sessionData?.session_id]);

    // Set call context when entering video call (includes session_mode once sessionData arrives)
    useEffect(() => {
        if (name && creatorId) {
            obs.setCallContext(creatorId, name, 'video', sessionData?.session_mode || null);
        }
        return () => {
            obs.clearCallContext();
        };
    }, [name, creatorId, sessionData?.session_mode]);

    // Audio playback refs
    const ringingAudioRef = useRef(null);
    const pickupAudioRef = useRef(null);
    const hangupAudioRef = useRef(null);
    const ringingIdRef = useRef(0);

    const formatTime = (seconds) => {
        const minutes = Math.floor(seconds / 60);
        const secs = seconds % 60;
        return `${minutes.toString().padStart(2, "0")}:${secs.toString().padStart(2, "0")}`;
    };
    const stopRingingAndPickup = () => {
        try {
            if (ringingAudioRef.current) {
                ringingAudioRef.current.stop();
                ringingAudioRef.current = null;
            }
        } catch (err) {
            console.error("Ringing audio stop error:", err);
        }

        try {
            if (pickupAudioRef.current) {
                pickupAudioRef.current.stop();
                pickupAudioRef.current = null;
            }
        } catch (err) {
            console.error("Pickup audio stop error:", err);
        }
    };

    const playHangupSound = async () => {
        try {
            if (hangupAudioRef.current) {
                hangupAudioRef.current.stop();
            }
            hangupAudioRef.current = await playAudioWithGlobalContext("/sounds/hangup.mp3");
        } catch (err) {
            console.error("Hangup audio play error:", err);
        }
    };

    useEffect(() => {
        let timerInterval;
        let isCancelled = false;

        const currentRingingId = ++ringingIdRef.current;

        const playRinging = async () => {
            try {
                if (ringingAudioRef.current) {
                    ringingAudioRef.current.stop();
                    ringingAudioRef.current = null;
                }

                const audioControl = await playAudioWithGlobalContext("/sounds/ringing2.mp3", { loop: true });

                if (isCancelled || currentRingingId !== ringingIdRef.current) {
                    console.log("[VideoChat] Ringing cancelled - stopping immediately");
                    audioControl.stop();
                    return;
                }

                ringingAudioRef.current = audioControl;
                console.log("[VideoChat] Ringing started");

            } catch (err) {
                console.error("Ringing audio play error:", err);
            }
        };
        // let ringTimeout;

        // if (shouldRing && !isCallActive) {
        //     console.log("[VideoChat] Ringing started → starting 20s timeout");

        //     ringTimeout = setTimeout(() => {
        //         console.log("[VideoChat] Ringing timeout → stopping session");

        //         stopSession(); // 🔥 end call after 20 sec
        //     }, 20000); // 20 seconds
        // }
        const playPickup = async () => {
            try {
                if (pickupAudioRef.current) {
                    pickupAudioRef.current.stop();
                    pickupAudioRef.current = null;
                }
                pickupAudioRef.current = await playAudioWithGlobalContext("/sounds/pickup.mp3");
                console.log("[VideoChat] Pickup sound played");
            } catch (err) {
                console.error("Pickup audio play error:", err);
            }
        };

        if (isCallActive) {
            console.log("[VideoChat] Call active - stopping ringing");

            if (ringingAudioRef.current) {
                ringingAudioRef.current.stop();
                ringingAudioRef.current = null;
            }

            playPickup();

            timerInterval = setInterval(() => {
                setCallDuration((prev) => prev + 1);
            }, 1000);

        } else if (shouldRing) {
            console.log("[VideoChat] Should ring - starting ringing");
            if (videoCallStatus === 'true') {
                Cookie.deleteCookie('video_call_start');
                stopSession()
                return; // ← stop here, don't start a new call
            }
            playRinging();
        } else {
            console.log("[VideoChat] Not ringing, not active - stopping all");
            stopRingingAndPickup();
        }

        return () => {
            console.log("[VideoChat] Cleanup - stopping ringing");
            isCancelled = true;

            if (ringingAudioRef.current) {
                ringingAudioRef.current.stop();
                ringingAudioRef.current = null;
            }

            if (timerInterval) {
                clearInterval(timerInterval);
            }
            // if (ringTimeout) {
            //     clearTimeout(ringTimeout);
            //     console.log("[VideoChat] Ringing timeout cleared");
            // }
        };
    }, [isCallActive, shouldRing,]);

    const requestMediaPermissions = async () => {
        try {
            setPermissionStatus("pending");

            const mediaStream = await window.navigator.mediaDevices.getUserMedia({
                audio: {
                    echoCancellation: true,
                    noiseSuppression: true,
                    autoGainControl: true,
                    sampleRate: 16000,
                },
                video: {
                    facingMode: { ideal: "user" },
                    width: { ideal: 1280 },
                    height: { ideal: 720 },
                },
            });

            audioStreamRef.current = mediaStream;
            const videoTrack = mediaStream.getVideoTracks()[0];
            setIsCameraOn(Boolean(videoTrack?.enabled));
            setPermissionStatus("granted");

            // #region agent log
            // H12 FIX: Create and resume AudioContext IMMEDIATELY after getUserMedia
            // This keeps the resume() call in the user gesture chain (iOS Safari requirement)
            // Before: AudioContext was created/resumed in startMicPipeline (too late, gesture context lost)
            if (!micCtxRef.current) {
                micCtxRef.current = new (window.AudioContext || window.webkitAudioContext)({
                    sampleRate: 16000,
                });
                console.log('[VideoChat] AudioContext created early', micCtxRef.current.state);
            }

            // Resume immediately while still in user gesture chain
            if (micCtxRef.current.state === "suspended") {
                try {
                    await micCtxRef.current.resume();
                    console.log('[VideoChat] AudioContext resumed early SUCCESS', micCtxRef.current.state);
                } catch (resumeErr) {
                    console.error('[VideoChat] AudioContext resume early FAILED', resumeErr?.message);
                }
            }

            return true;
        } catch (err) {
            console.error("Mic error", err);
            setPermissionStatus("denied");
            toast.error("Camera or microphone denied");
            return false;
        }
    };

    const clearRoomConnectTimeout = () => {
        if (roomConnectTimeoutRef.current) {
            clearTimeout(roomConnectTimeoutRef.current);
            roomConnectTimeoutRef.current = null;
        }
    };

    const startMicPipeline = async () => {
        if (!audioStreamRef.current) return;

        if (!micCtxRef.current) {
            micCtxRef.current = new (window.AudioContext || window.webkitAudioContext)({
                sampleRate: 16000,
            });
        }

        const micCtx = micCtxRef.current;

        if (micCtx.state === "suspended") {
            try {
                await micCtx.resume();
            } catch (err) {
                console.error("AudioContext resume failed:", err);
            }
        }

        if (!micCtx.audioWorkletLoaded) {
            try {
                await micCtx.audioWorklet.addModule("/mic-processor.js");
                micCtx.audioWorkletLoaded = true;
            } catch (err) {
                console.error("AudioWorklet load failed:", err);
                return;
            }
        }

        const source = micCtx.createMediaStreamSource(audioStreamRef.current);
        const workletNode = new AudioWorkletNode(micCtx, "mic-processor");

        workletNode.port.onmessage = (e) => {
            const dg = deepgramSocketRef.current;
            if (!dg || dg.readyState !== WebSocket.OPEN) return;

            // Track last audio byte time for latency calculations
            lastAudioByteTimeRef.current = Date.now();

            dg.send(e.data);
        };

        source.connect(workletNode);
        Cookie.setCookie('video_call_start', 'true')
        console.log("[VideoChat] Mic pipeline connected");
    };

    // const emitAvatarUserSpeechState = (state) => {
    //     const socket = getSocket();
    //     if (socket && creatorId) {
    //         socket.emit('avatar_user_speech_state', {
    //             creator_id: creatorId,
    //             state,
    //         });
    //     }
    // };

    const handleStartOfTurn = () => {
        console.log("User started speaking");
        // emitAvatarUserSpeechState('start');
        // Snapshot LiveKit state at turn start — used to guard send_message even if
        // LiveKit connects mid-utterance before EndOfTurn fires
        turnStartedWithLiveKitRef.current = isLiveKitConnectedRef.current;

        // Log turn detection latency
        if (lastAudioByteTimeRef.current) {
            const turnDetectionMs = Date.now() - lastAudioByteTimeRef.current;
            obs.emit(ObsEvent.DEEPGRAM_TURN_DETECTED, {
                turn_detection_ms: turnDetectionMs,
                status: 'success'
            });
        }

        // Interrupt while the avatar is speaking OR while a reply is still being generated;
        // the backend cancels the in-flight LLM+TTS turn either way.
        const replyAge = awaitingReplyRef.current > 0
            ? Date.now() - awaitingReplyRef.current
            : Infinity;
        const replyPending = replyAge < AWAITING_REPLY_MAX_MS;
        // A turn opening this soon after our own send is the rest of the same sentence, not
        // an interruption. handleEndOfTurn supersedes that reply once the continuation
        // lands, so exactly one reply is in flight and no overlapping speech is introduced.
        const isContinuation = replyPending && replyAge < BARGE_IN_MIN_REPLY_AGE_MS;
        if (avatarIsSpeakingRef.current || (replyPending && !isContinuation)) {
            console.log("Interruption detected! Stopping AI...");
            // Backend now owns the LiveAvatar WS, so interrupt is a single
            // Socket.IO event: the backend cancels active TTS AND sends
            // agent.interrupt to LiveAvatar in one place.
            awaitingReplyRef.current = 0;
            // A real barge-in starts a new thought, so the abandoned sentence must not be
            // prepended to whatever the user says next.
            pendingTranscriptRef.current = '';
            const socket = getSocket();
            if (socket) {
                socket.emit('interrupt_ai_response', { creator_id: creatorId });
            }
        }
    };

    const handleEndOfTurn = (transcript) => {
        if (!turnStartedWithLiveKitRef.current) {
            console.log('[VideoChat] Ignoring transcript — LiveKit was not connected when turn started');
            return;
        }
        console.log("User finished speaking:", transcript);
        // Calculate transcription latency
        const transcriptionMs = lastAudioByteTimeRef.current
            ? Date.now() - lastAudioByteTimeRef.current
            : null;

        // Inside the continuation window the earlier fragment's reply is still in flight and
        // this is the rest of that sentence: replace it rather than answer a fragment.
        const supersedes = awaitingReplyRef.current > 0
            && Date.now() - awaitingReplyRef.current < AWAITING_REPLY_MAX_MS;
        const carried = supersedes ? pendingTranscriptRef.current : '';
        const merged = [carried, transcript].filter(Boolean).join(' ').trim();

        // Generate turn_id for correlation
        const turnId = obs.startTurn();

        // Log transcription complete
        obs.emit(ObsEvent.DEEPGRAM_TRANSCRIPTION_COMPLETE, {
            transcription_ms: transcriptionMs,
            turn_id: turnId,
            transcript_length: merged?.length || 0,
            status: 'success'
        });

        const socket = getSocket();
        if (socket && creatorId) {
            // Cancel the fragment's reply here, where the fuller text is ready to send, so
            // the two never stream at once. StartOfTurn deliberately no longer does this.
            if (supersedes) {
                socket.emit('interrupt_ai_response', { creator_id: creatorId });
            }
            socket.emit("send_message", {
                message: merged,
                chat_id: chatId,
                creator_id: creatorId,
                mode: "video",
                turn_id: turnId,
                turn_start_time: obs.getTurn().turn_start_time,
            });
            // Reply now in flight: allows barge-in before the avatar audibly starts.
            awaitingReplyRef.current = Date.now();
            pendingTranscriptRef.current = merged;
        }
    };

    const connectToDeepgram = (token) => {
        deepgramConnectTrackerRef.current = new LatencyTracker().start();
        const socket = getSocket();
        const wsUrl =
            "wss://api.deepgram.com/v2/listen?model=flux-general-en&encoding=linear16&sample_rate=16000&eot_threshold=0.85&eot_timeout_ms=8000";
        const dg = new WebSocket(wsUrl, ["bearer", token]);
        deepgramSocketRef.current = dg;

        dg.onopen = () => {
            const connectionMs = deepgramConnectTrackerRef.current?.elapsed() || null;
            console.log("Connected to Deepgram");
            obs.emit(ObsEvent.DEEPGRAM_CONNECTED, {
                connection_ms: connectionMs,
                status: 'success'
            });

            startMicPipeline();
        };

        dg.onmessage = (evt) => {
            try {
                const data = JSON.parse(evt.data);
                if (data.type === "TurnInfo" && data.event === "StartOfTurn") {
                    handleStartOfTurn();
                }

                if (data.type === "TurnInfo" && data.event === "EndOfTurn") {
                    const transcript = data.transcript?.trim();
                    if (transcript) {
                        handleEndOfTurn(transcript);
                    }
                }
            } catch (e) {
                // ignore
            }
        };

        dg.onerror = (e) => {
            console.error("Deepgram WS error", e);
            obs.emit(ObsEvent.DEEPGRAM_CONNECTION_ERROR, {
                error_message: e?.message || 'Unknown error',
                status: 'error'
            });
            toast.error("Call failed. Please try again.");
            stopSessionRef.current?.("call_failed");
            socket.emit('get_message', {
                creator_id: creatorId,
                chat_id: chatId,
                scenario: 'call_failed'
            })
        };

        dg.onclose = (e) => {
            console.log("Deepgram closed", e?.code, e?.reason);
            // Code 1000 = normal closure, 1001 = going away (page close) - these are expected
            const isExpected = e?.code === 1000 || e?.code === 1001;
            obs.emit(ObsEvent.DEEPGRAM_DISCONNECTED, {
                code: e?.code,
                reason: e?.reason,
                was_clean: e?.wasClean,
                status: isExpected ? 'info' : 'error'
            });
        };
    };


    const stopSession = (from = "") => {
        if (isStoppingRef.current) return;
        isStoppingRef.current = true;

        Cookie.deleteCookie('video_call_start')
        clearRoomConnectTimeout();
        if (keepAliveIntervalRef.current) {
            console.log('[VideoChat] Clearing keep-alive interval in stopSession');
            clearInterval(keepAliveIntervalRef.current);
            keepAliveIntervalRef.current = null;
        }

        stopRingingAndPickup();
        setShouldRing(false);
        setIsCallActive(false);
        setCallPhase("ending");
        setPermissionStatus((prev) => prev === "denied" ? prev : "granted");
        isLiveKitConnectedRef.current = false;
        // No reply can be pending once the call is torn down.
        awaitingReplyRef.current = 0;
        // Otherwise the last sentence of this call would prefix the first of the next one.
        pendingTranscriptRef.current = '';
        setCallDuration(0);
        if (from !== "permission_denied") {
            playHangupSound();
        }

        try {
            micSourceRef.current?.disconnect();
            micSourceRef.current = null;
        } catch (e) { }

        try {
            micCtxRef.current?.close();
            micCtxRef.current = null;
        } catch (e) { }

        try {
            audioStreamRef.current?.getTracks().forEach((t) => t.stop());
            audioStreamRef.current = null;
        } catch (e) { }

        try {
            deepgramSocketRef.current?.close();
            deepgramSocketRef.current = null;
        } catch (e) { }

        const socket = getSocket();
        if (socket) {
            const payload = {
                session_id: sessionData?.session_id,
                session_token: sessionData?.session_token,
                creator_id: creatorId,
            };

            if (payload.session_id) {
                socket.emit('stop_video_call', payload);
            }
        }

        // Log video call ended
        obs.emit(ObsEvent.VIDEO_CALL_ENDED, {
            duration_sec: callDuration,
            session_id: sessionData?.session_id,
            status: 'success'
        });

        setSessionData(null);
        sessionProviderRef.current = null;
        tavusSessionRef.current = null;
        tavusAvatarJoinedRef.current = false;
        if (from == "limit_exceeded") {
            console.log('limit excided')
        } else {
            setTimeout(() => {
                navigator(`/user/chat?id=${creatorId}`, { replace: true });
            }, from === "permission_denied" ? 0 : 250);
        }

    };

    // Re-assigned every render: Deepgram/room/timer handlers are created once and call
    // through this ref, so it must hold the NEWEST stopSession closure. Commenting this
    // out in an unfinished May refactor silently disabled four teardown paths.
    stopSessionRef.current = stopSession;

    // Backend tears the LiveAvatar/media session down on Socket.IO disconnect. Do not keep
    // presenting an active call while the transport quietly reconnects underneath.
    useEffect(() => {
        return onSocketDisconnect(() => {
            if (isStoppingRef.current) return;
            toast.error("Connection lost. Call ended.");
            const stop = stopSessionRef.current;
            if (stop) stop("socket_disconnected");
        });
    }, []);

    const BACKGROUND_CALL_GRACE_MS = 30000;
    useEffect(() => {
        const clearTimer = () => {
            if (backgroundHideTimerRef.current) {
                clearTimeout(backgroundHideTimerRef.current);
                backgroundHideTimerRef.current = null;
            }
        };

        /** Tab in background, app switcher, or window lost focus (e.g. Alt+Tab on desktop). */
        const isCallOutOfFocus = () =>
            document.visibilityState === "hidden" || !document.hasFocus();

        const syncBackgroundTimer = () => {
            if (!isCallActive) return;

            if (!isCallOutOfFocus()) {
                clearTimer();
                return;
            }

            clearTimer();
            backgroundHideTimerRef.current = setTimeout(() => {
                backgroundHideTimerRef.current = null;
                if (isCallOutOfFocus()) {
                    stopSessionRef.current?.();
                }
            }, BACKGROUND_CALL_GRACE_MS);
        };

        const onWindowBlur = () => {
            setTimeout(syncBackgroundTimer, 0);
        };

        document.addEventListener("visibilitychange", syncBackgroundTimer);
        window.addEventListener("blur", onWindowBlur);
        window.addEventListener("focus", syncBackgroundTimer);
        window.addEventListener("pageshow", syncBackgroundTimer);

        return () => {
            document.removeEventListener("visibilitychange", syncBackgroundTimer);
            window.removeEventListener("blur", onWindowBlur);
            window.removeEventListener("focus", syncBackgroundTimer);
            window.removeEventListener("pageshow", syncBackgroundTimer);
            clearTimer();
        };
    }, [isCallActive]);

    const handleMute = () => {
        setMute(true);
        try {
            if (audioStreamRef.current) {
                audioStreamRef.current.getAudioTracks().forEach(track => {
                    track.enabled = false;
                });
            }
        } catch (e) {
            console.error("Mute error:", e);
        }
    };

    const handleUnmute = () => {
        setMute(false);
        try {
            if (audioStreamRef.current) {
                audioStreamRef.current.getAudioTracks().forEach(track => {
                    track.enabled = true;
                });
            }
        } catch (e) {
            console.error("Unmute error:", e);
        }
    };

    const handleKeepAliveSuccess = (data) => {
        console.log('[VideoChat] Keep-alive successful:', data);
        if (sessionProviderRef.current === "tavus") {
            return;
        }
        obs.emit(ObsEvent.HEYGEN_KEEP_ALIVE_SUCCESS, { status: 'success' });
    };

    const handleEndSession = () => {
        stopSession();
    };

    const handleGetResponse = (data) => {
        if (sessionProviderRef.current === "tavus") {
            return;
        }
        console.log("ai_response", data);
        if (streamRef.current && data?.message?.content) {
            // Log speak command for latency tracking
            obs.emit(ObsEvent.HEYGEN_SPEAK_COMMAND, {
                text_length: data.message.content.length,
                turn_id: obs.getTurn().turn_id,
            });

            streamRef.current.sendSpeakCommand(data.message.content);
        }
    };

    // Audio forwarding to LiveAvatar happens entirely on the backend now
    // (see app/services/liveavatar_ws.py). The frontend no longer handles
    // tts_audio_chunk / tts_audio_end in CUSTOM mode; avatar audio is heard
    // via the LiveKit subscription below.

    const handleAudioTokenResponse = (data) => {
        if (sessionProviderRef.current === "tavus") {
            return;
        }
        if (data?.success) {
            if (data?.token) connectToDeepgram(data.token);
        } else {
            stopSession()
            const socket = getSocket()
            socket.emit('get_message', {
                creator_id: creatorId,
                chat_id: chatId,
                scenario: 'call_failed'
            })
        }

    };

    // Handle LiveKit room connected - request greeting now that user can hear
    const handleRoomConnected = () => {
        // Our Daily join is not the avatar. Keep ringing until a remote participant arrives.
        if (sessionProviderRef.current === "tavus") {
            isLiveKitConnectedRef.current = true;
            return;
        }
        console.log('[VideoChat] Room connected, requesting greeting...');
        isLiveKitConnectedRef.current = true;
        clearRoomConnectTimeout();
        setCallPhase("connected");
        const socket = getSocket();
        if (socket && creatorId && sessionProviderRef.current !== "tavus") {
            socket.emit('get_greeting', {
                chat_id: chatId,
                creator_id: creatorId,
                mode: 'video'
            });
        }
    };

    // Handle avatar speaking state changes for e2e latency
    const handleAvatarSpeakingChange = (isSpeaking) => {
        setAvatarIsSpeaking(isSpeaking);

        if (isSpeaking) {
            // Reply arrived: from here the interrupt gate rides avatarIsSpeaking instead.
            awaitingReplyRef.current = 0;
            // The sentence has been answered, so it must not prefix the next question.
            pendingTranscriptRef.current = '';
            // Log E2E video TTFB (time from user's last audio to avatar starting to speak)
            obs.emitWithLatency(ObsEvent.E2E_VIDEO_TTFB, 'e2e_ttfb_ms', {
                status: 'success'
            });

            // Also log HeyGen speak started
            obs.emit(ObsEvent.HEYGEN_SPEAK_STARTED, {
                turn_id: obs.getTurn().turn_id,
                status: 'success'
            });
        } else {
            // Avatar stopped speaking - end the turn
            obs.emit(ObsEvent.HEYGEN_SPEAK_ENDED, {
                turn_id: obs.getTurn().turn_id,
                status: 'success'
            });
            obs.endTurn();
        }
    };

    useEffect(() => {
        const socket = getSocket();
        if (!socket || !creatorId) {
            return undefined;
        }

        let isMounted = true;

        const initializeCall = async () => {
            setCallPhase("requesting_permissions");
            const hasPermission = await requestMediaPermissions();
            if (!isMounted) return;

            if (!hasPermission) {
                stopSession("permission_denied");
                return;
            }

            setCallPhase("starting_call");
            setShouldRing(true);
            socket.emit('start_video_call', { creator_id: creatorId });
        };

        initializeCall();


        const handleVideoCallStarted = (sessionData) => {
            console.log('=== VIDEO SESSION DATA ===');
            console.log('PROVIDER:', sessionData?.provider);
            console.log('LIVEKIT_URL:', sessionData?.livekit_url);
            console.log('CONVERSATION_URL:', sessionData?.conversation_url);
            console.log('SESSION_MODE:', sessionData?.session_mode);
            console.log('FULL SESSION DATA:', sessionData);
            console.log('========================');
            if (sessionData?.success) {
                const isTavusSession = sessionData?.provider === "tavus" || Boolean(sessionData?.conversation_url);
                if (!isTavusSession && !sessionData?.livekit_url) {
                    console.error('ERROR: livekit_url is missing!');
                }

                sessionProviderRef.current = isTavusSession ? "tavus" : "heygen";
                setSessionData(sessionData);

                const startVideoKeepAlive = (liveSession) => {
                    if (keepAliveIntervalRef.current) {
                        clearInterval(keepAliveIntervalRef.current);
                    }
                    const sendKeepAlive = () => {
                        if (!isTavusSession) {
                            obs.emit(ObsEvent.HEYGEN_KEEP_ALIVE_SENT, {
                                session_id: liveSession.session_id,
                                status: 'info'
                            });
                        }
                        socket.emit('keep_session_alive', {
                            session_token: liveSession.session_token,
                            session_id: liveSession.session_id,
                            creator_id: creatorId,
                            session_type: 'video'
                        });
                    };
                    keepAliveIntervalRef.current = setInterval(sendKeepAlive, 5000);
                    sendKeepAlive();
                };

                obs.emit(ObsEvent.VIDEO_CALL_STARTED, {
                    session_id: sessionData?.session_id,
                    status: 'success'
                });

                if (isTavusSession) {
                    tavusSessionRef.current = sessionData;
                    tavusAvatarJoinedRef.current = false;
                    setShouldRing(true);
                    setCallPhase("starting_call");
                    clearRoomConnectTimeout();
                    roomConnectTimeoutRef.current = setTimeout(() => {
                        toast.error("The avatar didn't join. Please try again.");
                        stopSessionRef.current?.("avatar_join_timeout");
                    }, TAVUS_AVATAR_JOIN_TIMEOUT_MS);
                } else {
                    setShouldRing(false);
                    setCallDuration(0);
                    setIsCallActive(true);
                    setCallPhase("connecting_room");
                    clearRoomConnectTimeout();
                    roomConnectTimeoutRef.current = setTimeout(() => {
                        toast.error("Room connection failed");
                        stopSessionRef.current?.("room_connect_timeout");
                    }, 15000);
                    startVideoKeepAlive(sessionData);
                }

                onTavusAvatarJoinedRef.current = () => {
                    if (sessionProviderRef.current !== "tavus" || tavusAvatarJoinedRef.current) {
                        return;
                    }
                    const liveSession = tavusSessionRef.current;
                    if (!liveSession) {
                        return;
                    }
                    tavusAvatarJoinedRef.current = true;
                    clearRoomConnectTimeout();
                    setCallDuration(0);
                    setShouldRing(false);
                    setIsCallActive(true);
                    setCallPhase("connected");
                    startVideoKeepAlive(liveSession);
                };

                if (!isTavusSession) {
                    socket.emit("request_audio_token", { creator_id: creatorId, call_type: 'video' });
                }

                // NOTE: Greeting is NOT requested here - it's requested after LiveKit room connects
                // via the onRoomConnected callback in HeyGenStream, to ensure the user can hear it
            } else {
                socket.emit('get_message', {
                    creator_id: creatorId,
                    chat_id: chatId,
                    scenario: 'call_failed'
                })
                stopSession()
            }

        };

        socket.on('video_call_started', handleVideoCallStarted);
        socket.on('end_video_call', handleEndSession);
        socket.on('ai_response', handleGetResponse);  // FULL-mode text reply
        socket.on("audio_token_response", handleAudioTokenResponse);
        socket.on("keep_alive_success", handleKeepAliveSuccess);

        // LiveAvatar speak state relayed from the backend (which owns the
        // realtime WS in CUSTOM mode). This drives `avatarIsSpeaking` so the
        // interrupt gate in handleStartOfTurn knows when to fire.
        const handleAvatarSpeakStarted = () => handleAvatarSpeakingChange(true);
        const handleAvatarSpeakEnded = () => handleAvatarSpeakingChange(false);
        socket.on('avatar_speak_started', handleAvatarSpeakStarted);
        socket.on('avatar_speak_ended', handleAvatarSpeakEnded);

        const handleVideoCallEnded = (data) => {
            console.log("[VideoChat] video_call_ended received, status:", data?.status);
            // Ignore an end naming a DIFFERENT session: this handler is bound to the socket,
            // not to one call, so a late teardown would kill the call now on screen. Events
            // with no session_id (limit reached, duplicate-teardown ack) still apply.
            if (data?.session_id && sessionIdRef.current && data.session_id !== sessionIdRef.current) {
                console.log("[VideoChat] Ignoring video_call_ended for another session:", data.session_id);
                return;
            }
            if (data?.reason) toast.error(data.reason);
            if (data.status == "limit_exceeded") {
                stopSession("limit_exceeded");
                toggleForModal('TOGGLE_PAYROLL_MODAL')
                return
            }
            stopSession();
        };

        // Handle call ended event from server
        socket.on("video_call_ended", handleVideoCallEnded);

        return () => {
            isMounted = false;
            clearRoomConnectTimeout();

            if (keepAliveIntervalRef.current) {
                console.log('[VideoChat] Clearing keep-alive interval');
                clearInterval(keepAliveIntervalRef.current);
                keepAliveIntervalRef.current = null;
            }

            socket.off('video_call_started', handleVideoCallStarted);
            socket.off('end_video_call', handleEndSession);
            socket.off('ai_response', handleGetResponse);
            socket.off("audio_token_response", handleAudioTokenResponse);
            socket.off("keep_alive_success", handleKeepAliveSuccess);
            socket.off("video_call_ended", handleVideoCallEnded);
            socket.off('avatar_speak_started', handleAvatarSpeakStarted);
            socket.off('avatar_speak_ended', handleAvatarSpeakEnded);

            // Only emit stop_video_call here if stopSession() hasn't already
            // torn the call down (which clears sessionIdRef). Emitting again
            // without a session_id triggers a spurious "session_id is required"
            // error from the backend.
            const pendingSessionId = sessionIdRef.current;
            if (pendingSessionId) {
                socket.emit('stop_video_call', {
                    creator_id: creatorId,
                    session_id: pendingSessionId,
                });
                sessionIdRef.current = null;
            }

            try {
                micCtxRef.current?.close();
                audioStreamRef.current?.getTracks().forEach(t => t.stop());
                deepgramSocketRef.current?.close();
            } catch (e) { }
        };
    }, [creatorId]);

    useEffect(() => {
        isStoppingRef.current = false;
    }, [creatorId]);

    useEffect(() => {
        if (isInitialized == 'video_token_faild' || isInitialized == "failed") {
            const socket = getSocket();

            dispatch(setSocketState({
                user: null,
                isInitialized: 'connected',
            }));
            socket.emit('get_message', {
                creator_id: creatorId,
                chat_id: chatId,
                scenario: 'call_failed'
            })
            stopSession();
        }
    }, [isInitialized])

    useEffect(() => {
        if (isBackPressed) {
            stopSession()
        }
    }, [isBackPressed])

    const getCallStatusText = () => {
        if (isCallActive) {
            return formatTime(callDuration);
        }

        switch (callPhase) {
            case "requesting_permissions":
                return "Waiting for camera and microphone...";
            case "starting_call":
                return "Ringing...";
            case "connecting_room":
                return "Connecting room...";
            case "connected":
                return "Connected";
            case "ending":
                return "Ending call...";
            default:
                return shouldRing ? "Ringing..." : "Connecting...";
        }
    };

    const showLoaderOverlay = permissionStatus === "pending";

    return (
        <>
            {
                TOGGLE_PAYROLL_MODAL && <PayrollModal id={userId} name={name} stopAll={() => navigator(`/user/chat?id=${userId}`, { replace: true })} />
            }
            <div className="main-header-s1 center-text w-full absolute p-4 justify-center">
                <button type="button" className="back-btn" onClick={() => stopSession()}>
                    <img src="/images/icons/chevron-left.svg" alt="" />
                </button>
                <div className="centr-texts-div">
                    <h1>{name}</h1>
                    <h4>{getCallStatusText()}</h4>
                </div>
                {/* <button type="button" className="transparent-btn">
                    <RiUserAddFill />
                </button> */}
            </div>

            <div className="body-content w-full h-screen m-0 p-0">
                {showLoaderOverlay && (
                    <div className="absolute inset-0 z-[3] flex flex-col items-center justify-center gap-4 bg-black/40 backdrop-blur-md">
                        <div className="loader" style={{ width: 36, height: 36 }} />
                        <p className="m-0 text-base font-medium text-white">{getCallStatusText()}</p>
                    </div>
                )}
                {sessionData?.provider === "tavus" && sessionData?.conversation_url ? (
                    <div style={{ position: "relative", width: "100%", height: "100%", flex: 1, display: "flex", flexDirection: "column" }}>
                        <div
                            style={{
                                position: "absolute",
                                inset: 0,
                                opacity: isCallActive ? 1 : 0,
                                pointerEvents: isCallActive ? "auto" : "none",
                            }}
                        >
                            <TavusStream
                                ref={streamRef}
                                conversationUrl={sessionData?.conversation_url}
                                meetingToken={sessionData?.meeting_token}
                                sessionId={sessionData?.session_id}
                                mediaStream={audioStreamRef.current}
                                onRoomConnected={handleRoomConnected}
                                onRemoteParticipantJoined={() => onTavusAvatarJoinedRef.current()}
                                onRoomError={(message) => {
                                    toast.error(message || "Room connection failed");
                                    stopSessionRef.current?.("room_error");
                                }}
                            />
                        </div>
                        {!isCallActive && (
                            <div className="call-user-image">
                                <Avatar
                                    alt={name}
                                    src={profilePic}
                                    sx={{ width: 200, height: 200, fontSize: 80 }}
                                >
                                    {name?.charAt(0)}
                                </Avatar>
                            </div>
                        )}
                    </div>
                ) : isCallActive && sessionData?.livekit_url && sessionData?.livekit_client_token ? (
                    <HeyGenStream
                        ref={streamRef}
                        livekitUrl={sessionData?.livekit_url}
                        accessToken={sessionData?.livekit_client_token}
                        sessionId={sessionData?.session_id}
                        onAvatarSpeakingChange={handleAvatarSpeakingChange}
                        onRoomConnected={handleRoomConnected}
                        onRoomError={(message) => {
                            toast.error(message || "Room connection failed");
                            stopSessionRef.current?.("room_error");
                        }}
                    />
                ) : (
                    <div className="call-user-image">
                        <Avatar
                            alt={name}
                            src={profilePic}
                            sx={{ width: 200, height: 200, fontSize: 80 }}
                        >
                            {name?.charAt(0)}
                        </Avatar>
                    </div>
                )}
                <LocalCameraPreview
                    ref={childRef}
                    isCameraOn={isCameraOn}
                    setIsCameraOn={setIsCameraOn}
                    mediaStream={audioStreamRef.current}
                    permissionsGranted={permissionStatus === "granted"}
                />
            </div>

            <div className="call-footer-actions absolute left-0 right-0 bottom-5 mx-auto">
                <button
                    type="button"
                    className={`icon-btn`}
                    onClick={() => {
                        childRef.current.cameraSwitch();
                    }}
                >
                    {isCameraOn ? <VideocamIcon /> : <VideocamOffOutlinedIcon />}
                </button>

                <button
                    type='button'
                    className="icon-btn"
                    onClick={() => {
                        if (!mute) handleMute();
                        else handleUnmute();
                    }}
                >
                    {!mute ? <MdKeyboardVoice /> : <AiOutlineAudioMuted />}
                </button>

                <button
                    type="button"
                    className="icon-btn call-end"
                    onClick={stopSession}
                >
                    <MdCallEnd />
                </button>
            </div>
        </>
    );
}
